import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Save, ArrowLeft, GripVertical, Trash2, Type, AlignLeft, Hash, Calendar, Clock,
  ToggleLeft, List as ListIcon, Link2, Settings2, Loader2, Terminal, Eye, Table2, Plus, Star,
  LayoutGrid, X, Globe, Image as ImageIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from './axiosConfig';
import { resolveIcon, ALL_ICON_NAMES } from './iconResolver';

// 🎯 استوديو التطبيقات المخصصة - نفس منطق التفاعل بتاع WorkflowDesigner.js
// بالظبط (دوس على عنصر -> خصائصه تظهر في بانل جنبي ثابت)، مش تعديل مخفي
// خلف أيقونة جير في كل صف. الكانفاس نفسه WYSIWYG حقيقي - السكاشن بترسم
// بعدد الأعمدة الحقيقي بتاعها (grid-template-columns فعلي)، مش proxy للشكل.

const LANGUAGES = [
  { code: 'en', label: 'English' }, { code: 'ar', label: 'العربية' }, { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' }, { code: 'it', label: 'Italiano' }, { code: 'es', label: 'Español' },
  { code: 'zh', label: '中文' }, { code: 'ja', label: '日本語' }, { code: 'ko', label: '한국어' },
  { code: 'ru', label: 'Русский' }, { code: 'ur', label: 'اردو' },
];

// 🎯 الجداول الحقيقية المدعومة فعليًا في /lookups/{type} بالباك إند - قايمة
// مقفولة عمدًا (مش أي جدول عشوائي) عشان Link field ميرميش 404 صامت وقت
// التشغيل
const CORE_TABLES = [
  'locations', 'storerooms', 'assets', 'items', 'tools', 'itemstools', 'companies', 'sla',
  'purchaserequests', 'spareparts', 'roles', 'jobplans', 'failureclasses', 'failurecodes',
  'persons', 'labors', 'meters', 'crafts', 'materialrequest', 'safetyplans', 'inventory',
  'organizations', 'classifications', 'vendors', 'tax_category',
];

// 🎯 قايمة أيقونات جاهزة (بريديفاين) للتابات - نفس فكرة icon_name بتاع
// التطبيق ككل (اسم نصي من lucide-react) بس هنا مقفولة على شورت-ليست
// مناسبة لأسماء تابات شائعة بدل ما الأدمن يكتب اسم يدوي وممكن يغلط فيه
const TAB_ICONS = [
  'Layers', 'FileText', 'Info', 'ClipboardList', 'Settings2', 'Users', 'Calendar', 'Clock',
  'MapPin', 'Wrench', 'Truck', 'Package', 'DollarSign', 'ShieldCheck', 'AlertTriangle',
  'Paperclip', 'Image', 'Star', 'Tag', 'Database', 'Boxes', 'ListChecks', 'FolderOpen', 'CheckCircle',
];

// 🎯 دروب داون بيفتح شبكة أيقونات حقيقية (مش نص حر) - بتشوف الأيقونة نفسها
// جنب اختيارك، ولما تختار وحدة بتتفعل فورًا وبتظهر في الكانفاس وفي الفورمة
// وقت التشغيل جنب اسم التاب
const IconPickerDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const SelectedIcon = resolveIcon(value, null);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 border border-line rounded-lg px-3 py-2 text-[10px] font-bold bg-surface-app hover:border-primary transition-colors"
      >
        {SelectedIcon ? <SelectedIcon size={14} className="text-primary" /> : <span className="text-gray-400 italic">—</span>}
        <span className="text-ink-secondary">{value || 'None'}</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 start-0 z-50 w-64 bg-surface-card border border-line rounded-lg shadow-2xl p-2 grid grid-cols-6 gap-1">
            <button
              type="button"
              title="None"
              onClick={() => { onChange(''); setOpen(false); }}
              className="flex items-center justify-center p-2 rounded-lg text-gray-400 hover:bg-primary-soft transition-colors text-[9px]"
            >
              —
            </button>
            {TAB_ICONS.map((name) => {
              const Icon = resolveIcon(name);
              return (
                <button
                  key={name}
                  type="button"
                  title={name}
                  onClick={() => { onChange(name); setOpen(false); }}
                  className={`flex items-center justify-center p-2 rounded-lg transition-colors ${value === name ? 'bg-primary text-white' : 'text-ink-secondary hover:bg-primary-soft hover:text-primary'}`}
                >
                  <Icon size={16} />
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// 🎯 بيكر بيدور على أي أيقونة من كل أيقونات lucide-react (+1500 أيقونة) بدل
// ما الأدمن يكتب اسمها يدوي وممكن يغلط في الإملاء - نفس فكرة IconPickerDropdown
// بس مع سيرش وشبكة قابلة للسكرول عشان تستوعب العدد الكبير ده
const IconPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const SelectedIcon = resolveIcon(value, null);
  const filtered = search.trim()
    ? ALL_ICON_NAMES.filter((name) => name.toLowerCase().includes(search.trim().toLowerCase()))
    : ALL_ICON_NAMES;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 border border-line rounded-lg px-3 py-2 text-[10px] font-bold bg-surface-app hover:border-primary transition-colors"
      >
        {SelectedIcon ? <SelectedIcon size={14} className="text-primary" /> : <span className="text-gray-400 italic">—</span>}
        <span className="text-ink-secondary">{value || 'None'}</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 start-0 z-50 w-72 bg-surface-card border border-line rounded-lg shadow-2xl p-2 flex flex-col">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search icons..."
              className="w-full border border-line rounded px-2 py-1.5 text-[10px] font-bold mb-2 bg-surface-app"
            />
            <div className="grid grid-cols-6 gap-1 max-h-64 overflow-y-auto">
              <button
                type="button"
                title="None"
                onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
                className="flex items-center justify-center p-2 rounded-lg text-gray-400 hover:bg-primary-soft transition-colors text-[9px]"
              >
                —
              </button>
              {filtered.map((name) => {
                const Icon = resolveIcon(name);
                return (
                  <button
                    key={name}
                    type="button"
                    title={name}
                    onClick={() => { onChange(name); setOpen(false); setSearch(''); }}
                    className={`flex items-center justify-center p-2 rounded-lg transition-colors ${value === name ? 'bg-primary text-white' : 'text-ink-secondary hover:bg-primary-soft hover:text-primary'}`}
                  >
                    <Icon size={16} />
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <div className="col-span-6 text-center text-[10px] text-gray-400 py-4">No icons found</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const FIELD_TYPES = [
  { type: 'text', label: 'Text', icon: Type },
  { type: 'textarea', label: 'Textarea', icon: AlignLeft },
  { type: 'number', label: 'Number', icon: Hash },
  { type: 'date', label: 'Date', icon: Calendar },
  { type: 'datetime', label: 'Date & Time', icon: Clock },
  { type: 'boolean', label: 'Yes/No', icon: ToggleLeft },
  { type: 'select', label: 'Select', icon: ListIcon },
  { type: 'link', label: 'Link', icon: Link2 },
  { type: 'table', label: 'Table', icon: Table2 },
  { type: 'image', label: 'Image', icon: ImageIcon },
];

// 🎯 خانة نصية بتحوّل لـ array بس عند مغادرة الحقل (blur) - مش على كل حرف.
// كانت العلة إن التحويل لو حصل على كل keystroke، وقت ما تكتب فاصلة في
// الآخر بتتشال فورًا لأنها بتتفلتر كـ entry فاضي، فيبان إن الفاصلة "مش
// بترد" رغم إنها بتتكتب فعلاً
const ChoicesEditor = ({ value, onChange }) => {
  const [text, setText] = useState((value || []).join(', '));
  useEffect(() => { setText((value || []).join(', ')); }, [value]);
  return (
    <input
      className={propInputClass}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onChange(text.split(',').map((s) => s.trim()).filter(Boolean))}
    />
  );
};

// 🎯 مفتاح layout.sections لازم يتحدد بالتاب + اسم السكشن مع بعض - لو اتحدد
// باسم السكشن بس، أي حقول من غير section (المفتاح '' زي ما بيرجع من
// sectionName || 'General') في تابات مختلفة كانت بتشارك نفس الخانة في
// layout.sections، فتغيير عدد أعمدة سكشن في تاب كان بيغيّر كل السكاشن
// المسماة بنفس الاسم (أو من غير اسم خالص) في كل التابات التانية
const sectionLayoutKey = (tabName, sectionName) => `${tabName}::${sectionName || ''}`;

const PropLabel = ({ children }) => (
  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1">{children}</label>
);
const propInputClass = "w-full border-b-2 border-gray-100 focus:border-primary outline-none py-1.5 text-xs font-bold text-gray-700";

// 🎯 محرر ترجمات مشترك - للحقل ولاسم التطبيق. اليوزر هو اللي بيختار أي
// لغات يترجم، مفيش أي لغة إجبارية
const TranslationsEditor = ({ translations, onChange }) => {
  const current = translations || {};
  const usedCodes = Object.keys(current);
  const available = LANGUAGES.filter((l) => l.code !== 'en' && !usedCodes.includes(l.code));

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-gray-400">
        <Globe size={11} />
        <span className="text-[9px] font-black uppercase tracking-widest">Translations (optional)</span>
      </div>
      {usedCodes.map((code) => (
        <div key={code} className="flex items-center gap-2">
          <span className="text-[9px] font-black text-gray-400 uppercase w-8 shrink-0">{code}</span>
          <input
            className="flex-1 border-b border-gray-200 outline-none py-1 text-xs font-bold min-w-0"
            value={current[code]}
            onChange={(e) => onChange({ ...current, [code]: e.target.value })}
          />
          <button
            type="button"
            onClick={() => { const next = { ...current }; delete next[code]; onChange(next); }}
            className="text-gray-300 hover:text-status-danger shrink-0"
          >
            <X size={12} />
          </button>
        </div>
      ))}
      {available.length > 0 && (
        <select
          value=""
          onChange={(e) => { if (e.target.value) onChange({ ...current, [e.target.value]: '' }); }}
          className="text-[10px] font-bold text-primary border-b border-gray-200 outline-none py-1"
        >
          <option value="">+ Add language</option>
          {available.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
      )}
    </div>
  );
};

const FieldChip = ({ field, isSelected, onSelect, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const typeInfo = FIELD_TYPES.find((f) => f.type === field.field_type) || FIELD_TYPES[0];
  const TypeIcon = typeInfo.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onSelect(field.id)}
      className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border cursor-pointer transition-all min-w-0 ${isSelected ? 'border-primary bg-primary-soft ring-2 ring-primary/30' : 'border-line bg-surface-card hover:border-primary/50'}`}
    >
      <button {...attributes} {...listeners} onClick={(e) => e.stopPropagation()} className="cursor-grab text-gray-300 hover:text-gray-500 active:cursor-grabbing shrink-0">
        <GripVertical size={13} />
      </button>
      <TypeIcon size={13} className="text-primary shrink-0" />
      <span className="font-bold text-[11px] text-ink truncate flex-1">{field.label}</span>
      {field.is_primary && <Star size={10} className="text-status-warning shrink-0" fill="currentColor" />}
      {field.is_required && <span className="text-status-danger text-[11px] shrink-0">*</span>}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(field.id); }}
        className="text-gray-300 hover:text-status-danger shrink-0"
        title="Delete field"
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
};

const SectionBlock = ({ sectionName, sectionFields, columns, isSelected, selectedFieldId, onSelectSection, onSelectField, onDeleteField, onReorder }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sectionFields.findIndex((f) => f.id === active.id);
    const newIndex = sectionFields.findIndex((f) => f.id === over.id);
    onReorder(arrayMove(sectionFields, oldIndex, newIndex), sectionFields);
  };

  return (
    <div className={`border rounded-lg overflow-hidden ${isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-line'}`}>
      <button
        onClick={() => onSelectSection(sectionName)}
        className="w-full bg-surface-panel px-3 py-2 border-b border-line text-left hover:bg-primary-soft/50 transition-colors flex items-center gap-2"
      >
        <LayoutGrid size={11} className="text-gray-400" />
        <span className="text-[10px] font-black text-ink uppercase tracking-widest">{sectionName || 'General'}</span>
      </button>
      <div className="p-3">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sectionFields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
              {sectionFields.map((field) => (
                <FieldChip key={field.id} field={field} isSelected={field.id === selectedFieldId} onSelect={onSelectField} onDelete={onDeleteField} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

const CustomAppStudio = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { slug: slugParam } = useParams();
  const isNew = !slugParam || slugParam === 'new';

  const [meta, setMeta] = useState({ slug: '', label: '', icon_name: 'Cpu', category: 'Custom Applications', layout: { sections: {} } });
  const [fields, setFields] = useState([]);
  const [otherModels, setOtherModels] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [customTabs, setCustomTabs] = useState(['General']);
  const [newTabName, setNewTabName] = useState('');
  const [activeCanvasTab, setActiveCanvasTab] = useState('General');
  const [selected, setSelected] = useState({ type: 'form' });

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await api.get(`/custom-models/${slugParam}`);
      setMeta(res.data);
      setFields(res.data.fields || []);
    } catch (err) {
      toast.error(t('customApps.loadFailed', 'Failed to load this application'));
    } finally {
      setLoading(false);
    }
  }, [slugParam, isNew, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/custom-models', { params: { published_only: false } })
      .then((res) => setOtherModels((res.data || []).filter((m) => m.slug !== slugParam)))
      .catch(() => setOtherModels([]));
    api.get('/domains', { params: { limit: 200 } })
      .then((res) => setDomains(res.data?.data || []))
      .catch(() => setDomains([]));
  }, [slugParam]);

  const groupedByTab = useMemo(() => {
    const tabs = {};
    fields.forEach((f) => {
      const tabName = f.tab || 'General';
      if (!tabs[tabName]) tabs[tabName] = [];
      tabs[tabName].push(f);
    });
    return tabs;
  }, [fields]);

  const allTabNames = useMemo(
    () => Array.from(new Set([...customTabs, ...Object.keys(groupedByTab)])),
    [customTabs, groupedByTab]
  );

  useEffect(() => {
    if (!allTabNames.includes(activeCanvasTab)) setActiveCanvasTab(allTabNames[0] || 'General');
  }, [allTabNames, activeCanvasTab]);

  const activeTabFields = groupedByTab[activeCanvasTab] || [];

  const groupedBySection = useMemo(() => {
    const sections = {};
    activeTabFields.forEach((f) => {
      const key = f.section || '';
      if (!sections[key]) sections[key] = [];
      sections[key].push(f);
    });
    return sections;
  }, [activeTabFields]);

  const layout = meta.layout || { sections: {} };
  const selectedField = selected.type === 'field' ? fields.find((f) => f.id === selected.fieldId) : null;

  const handleCreateModel = async () => {
    if (!meta.slug || !meta.label) {
      toast.error(t('customApps.slugLabelRequired', 'Slug and label are required'));
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/custom-models', meta);
      toast.success(t('customApps.created', 'Application created'));
      navigate(`/admin/custom-apps/${res.data.slug}/studio`, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('customApps.createFailed', 'Failed to create application'));
    } finally {
      setSaving(false);
    }
  };

  // 🎯 مبنعملش setMeta(res.data) بعد النجاح عمدًا - لو اليوزر دبّس أكتر من
  // checkbox بسرعة، أكتر من طلب PUT بيبقوا شغالين في نفس الوقت، وأي رد
  // متأخر كان بيرجّع meta لحالة أقدم وبيلغي تعديل تاني حصل بعده (بيبان إن
  // الخيارات "بترجع لوحدها"). الـ optimistic update اللي فوق كافي، والـ
  // PUT بس بيبعت المفتاح ده لوحده فمفيش داعي نستنى رد كامل نرجّعه
  const handleUpdateModelField = async (key, value) => {
    setMeta((prev) => ({ ...prev, [key]: value }));
    try {
      await api.put(`/custom-models/${slugParam}`, { [key]: value });
    } catch (err) {
      toast.error(t('customApps.updateFailed', 'Failed to save'));
    }
  };

  const handleAddField = async (fieldType) => {
    const typeInfo = FIELD_TYPES.find((f) => f.type === fieldType);
    // اسم فريد فعليًا - مش بس بناءً على العدد الحالي، عشان لو حقل في النص
    // اتمسح مش هيتصادم مع اسم حقل تاني لسه موجود
    let n = fields.length + 1;
    while (fields.some((f) => f.field_name === `${fieldType}_${n}`)) n += 1;
    // 🎯 لو في سكشن محدد دلوقتي، الحقل الجديد بيتحط جواه مباشرة - قبل كده
    // كان دايمًا بيتحط من غير section (null)، فلو كنت مختار سكشن معيّن
    // وضفت حقل، كان بيروح لمجموعة "من غير section" منفصلة تمامًا بدل ما
    // يتحط جنب زمايله في نفس السكشن
    const sectionForNewField = selected.type === 'section' ? (selected.sectionName || null) : undefined;
    try {
      const res = await api.post(`/custom-models/${slugParam}/fields`, {
        field_name: `${fieldType}_${n}`,
        label: typeInfo?.label || fieldType,
        field_type: fieldType,
        tab: activeCanvasTab === 'General' ? null : activeCanvasTab,
        section: sectionForNewField,
      });
      setFields((prev) => [...prev, res.data]);
      setSelected({ type: 'field', fieldId: res.data.id });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('customApps.addFieldFailed', 'Failed to add field'));
    }
  };

  const handleUpdateField = (updated) => {
    setFields((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
    api.put(`/custom-fields/${updated.id}`, {
      label: updated.label, is_required: updated.is_required, options: updated.options,
      tab: updated.tab, section: updated.section, column_index: updated.column_index,
      translations: updated.translations, show_in_list: updated.show_in_list, is_primary: updated.is_primary,
      is_readonly: updated.is_readonly, col_span: updated.col_span,
    }).catch(() => toast.error(t('customApps.updateFieldFailed', 'Failed to save field')));
  };

  const handleDeleteField = async (fieldId) => {
    setFields((prev) => prev.filter((f) => f.id !== fieldId));
    if (selected.type === 'field' && selected.fieldId === fieldId) setSelected({ type: 'form' });
    try {
      await api.delete(`/custom-fields/${fieldId}`);
    } catch (err) {
      toast.error(t('customApps.deleteFieldFailed', 'Failed to delete field'));
    }
  };

  const handleReorderInSection = (reordered, originalSectionFields) => {
    const originalOrders = originalSectionFields.map((f) => f.sort_order).sort((a, b) => a - b);
    const updated = reordered.map((f, i) => ({ ...f, sort_order: originalOrders[i] }));
    setFields((prev) => prev.map((f) => updated.find((u) => u.id === f.id) || f));
    updated.forEach((f) => { api.put(`/custom-fields/${f.id}`, { sort_order: f.sort_order }).catch(() => {}); });
  };

  const handleAddTab = () => {
    const name = newTabName.trim();
    if (!name || allTabNames.includes(name)) return;
    setCustomTabs((prev) => [...prev, name]);
    setActiveCanvasTab(name);
    setSelected({ type: 'tab', tabName: name });
    setNewTabName('');
  };

  const handleRenameTab = (oldName, newName) => {
    const name = newName.trim();
    if (!name || name === oldName) return;
    const affected = fields.filter((f) => (f.tab || 'General') === oldName);
    const stored = name === 'General' ? null : name;
    setFields((prev) => prev.map((f) => ((f.tab || 'General') === oldName ? { ...f, tab: stored } : f)));
    affected.forEach((f) => { api.put(`/custom-fields/${f.id}`, { tab: stored }).catch(() => {}); });
    setCustomTabs((prev) => prev.map((tName) => (tName === oldName ? name : tName)));
    setActiveCanvasTab(name);
    setSelected({ type: 'tab', tabName: name });
  };

  const handleToggleTabHidden = (tabName) => {
    const tabs = { ...(layout.tabs || {}) };
    tabs[tabName] = { ...(tabs[tabName] || {}), hidden: !tabs[tabName]?.hidden };
    handleUpdateModelField('layout', { ...layout, tabs });
  };

  const handleSetTabIcon = (tabName, iconName) => {
    const tabs = { ...(layout.tabs || {}) };
    tabs[tabName] = { ...(tabs[tabName] || {}), icon: iconName || null };
    handleUpdateModelField('layout', { ...layout, tabs });
  };

  const handleSetTabTranslations = (tabName, translations) => {
    const tabs = { ...(layout.tabs || {}) };
    tabs[tabName] = { ...(tabs[tabName] || {}), translations };
    handleUpdateModelField('layout', { ...layout, tabs });
  };

  const handleRenameSection = (oldName, newName) => {
    const key = newName.trim() || null;
    const affected = activeTabFields.filter((f) => (f.section || '') === oldName);
    setFields((prev) => prev.map((f) => (affected.includes(f) ? { ...f, section: key } : f)));
    affected.forEach((f) => { api.put(`/custom-fields/${f.id}`, { section: key }).catch(() => {}); });
    const sections = { ...(layout.sections || {}) };
    const oldKey = sectionLayoutKey(activeCanvasTab, oldName);
    const newKey = sectionLayoutKey(activeCanvasTab, key || '');
    if (sections[oldKey]) { sections[newKey] = sections[oldKey]; delete sections[oldKey]; }
    handleUpdateModelField('layout', { ...layout, sections });
    setSelected({ type: 'section', sectionName: key || '' });
  };

  const handleDeleteSection = (sectionName) => {
    const affected = activeTabFields.filter((f) => (f.section || '') === sectionName);
    setFields((prev) => prev.map((f) => (affected.includes(f) ? { ...f, section: null } : f)));
    affected.forEach((f) => { api.put(`/custom-fields/${f.id}`, { section: null }).catch(() => {}); });
    setSelected({ type: 'form' });
  };

  const handleSetSectionColumns = (sectionName, columns) => {
    const sections = { ...(layout.sections || {}) };
    const key = sectionLayoutKey(activeCanvasTab, sectionName);
    sections[key] = { ...(sections[key] || {}), columns };
    handleUpdateModelField('layout', { ...layout, sections });
  };

  const handleSetSectionTranslations = (sectionName, translations) => {
    const sections = { ...(layout.sections || {}) };
    const key = sectionLayoutKey(activeCanvasTab, sectionName);
    sections[key] = { ...(sections[key] || {}), translations };
    handleUpdateModelField('layout', { ...layout, sections });
  };

  const handleTogglePublish = async () => {
    try {
      const res = await api.put(`/custom-models/${slugParam}`, { is_published: !meta.is_published });
      setMeta(res.data);
      toast.success(res.data.is_published ? t('customApps.published', 'Published') : t('customApps.unpublished', 'Unpublished'));
    } catch (err) {
      toast.error(t('customApps.publishFailed', 'Failed to update'));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full text-gray-400"><Loader2 size={22} className="animate-spin" /></div>;
  }

  if (isNew) {
    return (
      <div className="flex flex-col h-full bg-surface-app">
        <div className="bg-surface-card p-4 border-b shadow-card flex items-center gap-3">
          <button onClick={() => navigate('/admin/custom-apps')} className="text-gray-400 hover:text-primary transition-colors"><ArrowLeft size={20} /></button>
          <h2 className="text-primary font-black italic uppercase text-lg tracking-tighter">{t('customApps.newApp', 'New Custom Application')}</h2>
        </div>
        <div className="p-6 max-w-lg mx-auto w-full space-y-4">
          <div className="flex flex-col gap-1">
            <PropLabel>{t('customApps.form.label', 'Label')}</PropLabel>
            <input
              className={propInputClass}
              placeholder={t('customApps.form.labelPlaceholder', 'e.g. Vehicles')}
              value={meta.label}
              onChange={(e) => {
                const label = e.target.value;
                const autoSlug = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
                setMeta((prev) => ({ ...prev, label, slug: prev.slugTouched ? prev.slug : autoSlug }));
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <PropLabel>{t('customApps.form.slug', 'Slug (URL identifier)')}</PropLabel>
            <input className={propInputClass} placeholder="vehicles" value={meta.slug}
              onChange={(e) => setMeta((prev) => ({ ...prev, slug: e.target.value.toLowerCase(), slugTouched: true }))} />
          </div>
          <div className="flex flex-col gap-1">
            <PropLabel>{t('customApps.form.icon', 'Icon')}</PropLabel>
            <IconPicker value={meta.icon_name} onChange={(name) => setMeta((prev) => ({ ...prev, icon_name: name }))} />
          </div>
          <div className="flex flex-col gap-1">
            <PropLabel>{t('customApps.form.category', 'Category')}</PropLabel>
            <input className={propInputClass} value={meta.category}
              onChange={(e) => setMeta((prev) => ({ ...prev, category: e.target.value }))} />
          </div>
          <div className="pt-2">
            <button onClick={handleCreateModel} disabled={saving}
              className="bg-primary text-white px-8 py-2.5 rounded-control font-black flex items-center gap-2 uppercase hover:bg-primary-hover transition-all shadow-lg active:scale-95 disabled:opacity-50">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {t('common.create', 'Create')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-surface-app overflow-hidden">
      <div className="bg-surface-card p-4 border-b shadow-card flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/custom-apps')} className="text-gray-400 hover:text-primary transition-colors"><ArrowLeft size={20} /></button>
          <h2 className="text-primary font-black italic uppercase text-lg tracking-tighter">{meta.label}</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.open(`/custom/${slugParam}`, '_blank', 'noopener,noreferrer')}
            disabled={fields.length === 0}
            title={fields.length === 0 ? t('customApps.previewNeedsFields', 'Add at least one field first') : ''}
            className="px-4 py-2 rounded-control font-black text-[10px] uppercase transition-all bg-surface-panel text-ink-secondary hover:bg-primary-soft flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Eye size={14} /> {t('customApps.preview', 'Preview')}
          </button>
          <button
            onClick={() => navigate(`/admin/custom-apps/${slugParam}/scripts`)}
            className="px-4 py-2 rounded-control font-black text-[10px] uppercase transition-all bg-surface-panel text-ink-secondary hover:bg-primary-soft flex items-center gap-1.5"
          >
            <Terminal size={14} /> {t('customApps.scripts.title', 'Action Scripts')}
          </button>
          <button
            onClick={handleTogglePublish}
            className={`px-4 py-2 rounded-control font-black text-[10px] uppercase transition-all ${meta.is_published ? 'bg-status-success-bg text-status-success' : 'bg-status-warning-bg text-status-warning'}`}
          >
            {meta.is_published ? t('customApps.published', 'Published') : t('customApps.draftPublish', 'Draft — Publish')}
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* الكانفاس */}
        <div className="flex-1 overflow-auto p-6 space-y-4">
          <div className="flex flex-wrap gap-2">
            {FIELD_TYPES.map((ft) => {
              const Icon = ft.icon;
              return (
                <button key={ft.type} onClick={() => handleAddField(ft.type)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-line bg-surface-card text-[10px] font-bold uppercase hover:bg-primary-soft transition-all">
                  <Icon size={14} /> {ft.label}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setSelected({ type: 'form' })}
            className={`w-full text-left px-3 py-2 rounded-lg border flex items-center gap-2 transition-all ${selected.type === 'form' ? 'border-primary bg-primary-soft ring-2 ring-primary/20' : 'border-line bg-surface-card hover:border-primary/50'}`}
          >
            <Settings2 size={13} className="text-primary" />
            <span className="text-[10px] font-black uppercase tracking-widest text-ink">{t('customApps.formProperties', 'Form Properties')}</span>
          </button>

          <div className="flex items-center gap-1.5 border-b border-line pb-2 flex-wrap">
            {allTabNames.map((tabName) => {
              const TabIcon = resolveIcon(layout.tabs?.[tabName]?.icon, null);
              return (
                <button
                  key={tabName}
                  onClick={() => { setActiveCanvasTab(tabName); setSelected({ type: 'tab', tabName }); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t-lg text-[10px] font-black uppercase transition-all ${activeCanvasTab === tabName ? 'bg-primary text-white' : 'bg-surface-panel text-gray-500 hover:bg-primary-soft'} ${layout.tabs?.[tabName]?.hidden ? 'opacity-40' : ''}`}
                >
                  {TabIcon && <TabIcon size={12} />}
                  {tabName} {groupedByTab[tabName] && <span className="opacity-60">({groupedByTab[tabName].length})</span>}
                </button>
              );
            })}
            <div className="flex items-center gap-1">
              <input
                value={newTabName}
                onChange={(e) => setNewTabName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTab(); } }}
                placeholder={t('customApps.newTab', 'New tab name')}
                className="w-28 border-b border-gray-200 outline-none py-1 text-[10px] font-bold"
              />
              <button onClick={handleAddTab} className="text-primary hover:text-primary-hover"><Plus size={14} /></button>
            </div>
          </div>

          <div className="space-y-3">
            {Object.entries(groupedBySection).map(([sectionName, sectionFields]) => (
              <SectionBlock
                key={sectionName || '__none__'}
                sectionName={sectionName}
                sectionFields={sectionFields}
                columns={layout.sections?.[sectionLayoutKey(activeCanvasTab, sectionName)]?.columns || 2}
                isSelected={selected.type === 'section' && selected.sectionName === sectionName}
                selectedFieldId={selected.type === 'field' ? selected.fieldId : null}
                onSelectSection={(name) => setSelected({ type: 'section', sectionName: name })}
                onSelectField={(fieldId) => setSelected({ type: 'field', fieldId })}
                onDeleteField={handleDeleteField}
                onReorder={handleReorderInSection}
              />
            ))}
            {activeTabFields.length === 0 && (
              <div className="text-center py-12 text-gray-400 text-xs font-bold uppercase">
                {t('customApps.noFields', 'Click a field type above to add your first field')}
              </div>
            )}
          </div>
        </div>

        {/* بانل الخصائص - ثابت جنب الكانفاس، بيتغيّر حسب العنصر المختار */}
        <div className="w-80 border-l border-line bg-surface-card overflow-auto shrink-0 p-4 space-y-4">
          {selected.type === 'form' && (
            <>
              <span className="text-[10px] font-black text-primary uppercase tracking-widest block">{t('customApps.formProperties', 'Form Properties')}</span>
              <div><PropLabel>{t('customApps.studio.label', 'Label')}</PropLabel><input className={propInputClass} value={meta.label} onChange={(e) => setMeta((p) => ({ ...p, label: e.target.value }))} onBlur={(e) => handleUpdateModelField('label', e.target.value)} /></div>
              <TranslationsEditor translations={meta.translations} onChange={(t2) => handleUpdateModelField('translations', t2)} />
              <div><PropLabel>{t('customApps.studio.icon', 'Icon')}</PropLabel><IconPicker value={meta.icon_name} onChange={(name) => { setMeta((p) => ({ ...p, icon_name: name })); handleUpdateModelField('icon_name', name); }} /></div>
              <div><PropLabel>{t('customApps.studio.category', 'Category')}</PropLabel><input className={propInputClass} value={meta.category} onChange={(e) => setMeta((p) => ({ ...p, category: e.target.value }))} onBlur={(e) => handleUpdateModelField('category', e.target.value)} /></div>
              <div className="space-y-2 pt-2 border-t border-line-subtle">
                <label className="flex items-center gap-2 text-[10px] font-bold text-gray-600">
                  <input
                    type="checkbox"
                    checked={layout.show_title !== false}
                    onChange={(e) => handleUpdateModelField('layout', { ...layout, show_title: e.target.checked })}
                    className="accent-primary"
                  />
                  {t('customApps.props.showTitle', 'Show title bar on the form')}
                </label>
                {[
                  ['is_hidden', t('customApps.props.hidden', 'Hidden (not shown in nav)')],
                  ['accepts_attachments', t('customApps.props.attachments', 'Accepts Attachments')],
                  ['accepts_workflow', t('customApps.props.workflow', 'Accepts Workflow (flag only, no engine yet)')],
                  ['accepts_reports', t('customApps.props.reports', 'Has Reports')],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 text-[10px] font-bold text-gray-600">
                    <input type="checkbox" checked={Boolean(meta[key])} onChange={(e) => handleUpdateModelField(key, e.target.checked)} className="accent-primary" />
                    {label}
                  </label>
                ))}
                <label className="flex items-center gap-2 text-[10px] font-bold text-gray-600">
                  <input
                    type="checkbox"
                    checked={Boolean(layout.status?.enabled)}
                    onChange={(e) => handleUpdateModelField('layout', { ...layout, status: { ...layout.status, enabled: e.target.checked } })}
                    className="accent-primary"
                  />
                  {t('customApps.props.acceptsStatus', 'Accepts Status Change')}
                </label>
                {layout.status?.enabled && (
                  <div className="ps-6">
                    <PropLabel>{t('customApps.studio.statusDomain', 'Status values come from')}</PropLabel>
                    <select
                      className={propInputClass}
                      value={layout.status?.domain_id || ''}
                      onChange={(e) => handleUpdateModelField('layout', { ...layout, status: { ...layout.status, domain_id: e.target.value } })}
                    >
                      <option value="">— {t('customApps.studio.selectDomain', 'Select a domain')} —</option>
                      {domains.map((d) => <option key={d.domain_id} value={d.domain_id}>{d.domain_id}{d.description ? ` - ${d.description}` : ''}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </>
          )}

          {selected.type === 'tab' && (
            <>
              <span className="text-[10px] font-black text-primary uppercase tracking-widest block">{t('customApps.studio.tab', 'Tab')}</span>
              <div>
                <PropLabel>{t('customApps.studio.name', 'Name')}</PropLabel>
                <input
                  key={selected.tabName}
                  className={propInputClass}
                  defaultValue={selected.tabName}
                  onBlur={(e) => handleRenameTab(selected.tabName, e.target.value)}
                />
              </div>
              <div>
                <PropLabel>{t('customApps.props.tabIcon', 'Icon')}</PropLabel>
                <IconPickerDropdown
                  key={selected.tabName}
                  value={layout.tabs?.[selected.tabName]?.icon || ''}
                  onChange={(iconName) => handleSetTabIcon(selected.tabName, iconName)}
                />
              </div>
              <TranslationsEditor
                key={`${selected.tabName}-tr`}
                translations={layout.tabs?.[selected.tabName]?.translations}
                onChange={(tr) => handleSetTabTranslations(selected.tabName, tr)}
              />
              <label className="flex items-center gap-2 text-[10px] font-bold text-gray-600">
                <input type="checkbox" checked={Boolean(layout.tabs?.[selected.tabName]?.hidden)} onChange={() => handleToggleTabHidden(selected.tabName)} className="accent-primary" />
                {t('customApps.props.hidden', 'Hidden')}
              </label>
            </>
          )}

          {selected.type === 'section' && (
            <>
              <span className="text-[10px] font-black text-primary uppercase tracking-widest block">{t('customApps.studio.section', 'Section')}</span>
              <div>
                <PropLabel>{t('customApps.studio.name', 'Name')}</PropLabel>
                <input
                  key={selected.sectionName}
                  className={propInputClass}
                  defaultValue={selected.sectionName}
                  placeholder="General"
                  onBlur={(e) => handleRenameSection(selected.sectionName, e.target.value)}
                />
              </div>
              <TranslationsEditor
                key={`${selected.sectionName}-tr`}
                translations={layout.sections?.[sectionLayoutKey(activeCanvasTab, selected.sectionName)]?.translations}
                onChange={(tr) => handleSetSectionTranslations(selected.sectionName, tr)}
              />
              <div>
                <PropLabel>{t('customApps.studio.columns', 'Columns')}</PropLabel>
                <div className="flex gap-1.5">
                  {[1, 2, 3].map((n) => (
                    <button key={n} onClick={() => handleSetSectionColumns(selected.sectionName, n)}
                      className={`flex-1 py-1.5 rounded text-[10px] font-black ${(layout.sections?.[sectionLayoutKey(activeCanvasTab, selected.sectionName)]?.columns || 2) === n ? 'bg-primary text-white' : 'bg-surface-panel text-gray-500'}`}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={() => handleDeleteSection(selected.sectionName)} className="text-[10px] font-black text-status-danger uppercase flex items-center gap-1.5 hover:underline">
                <Trash2 size={12} /> {t('customApps.deleteSection', 'Delete section (fields become uncategorized)')}
              </button>
            </>
          )}

          {selected.type === 'field' && selectedField && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black text-primary uppercase tracking-widest">{t('customApps.studio.field', 'Field')}</span>
                <button onClick={() => handleDeleteField(selectedField.id)} className="text-gray-300 hover:text-status-danger"><Trash2 size={14} /></button>
              </div>
              <div><PropLabel>{t('customApps.studio.label', 'Label')}</PropLabel><input className={propInputClass} value={selectedField.label} onChange={(e) => handleUpdateField({ ...selectedField, label: e.target.value })} /></div>
              <div><PropLabel>{t('customApps.studio.fieldName', 'Field name')}</PropLabel><input className={`${propInputClass} opacity-50`} value={selectedField.field_name} readOnly /></div>
              <TranslationsEditor translations={selectedField.translations} onChange={(t2) => handleUpdateField({ ...selectedField, translations: t2 })} />

              <div className="grid grid-cols-2 gap-2">
                <div><PropLabel>{t('customApps.studio.tab', 'Tab')}</PropLabel>
                  <select className={propInputClass} value={selectedField.tab || 'General'} onChange={(e) => handleUpdateField({ ...selectedField, tab: e.target.value === 'General' ? null : e.target.value })}>
                    {allTabNames.map((tn) => <option key={tn} value={tn}>{tn}</option>)}
                  </select>
                </div>
                <div><PropLabel>{t('customApps.studio.section', 'Section')}</PropLabel>
                  <input className={propInputClass} placeholder="General" value={selectedField.section || ''} onChange={(e) => handleUpdateField({ ...selectedField, section: e.target.value || null })} />
                </div>
              </div>

              <div>
                <PropLabel>{t('customApps.studio.fieldWidth', 'Width (columns to span)')}</PropLabel>
                <input
                  type="number" min="1" className={propInputClass}
                  placeholder={t('customApps.studio.fieldWidthAuto', 'Auto')}
                  value={selectedField.col_span ?? ''}
                  onChange={(e) => handleUpdateField({ ...selectedField, col_span: e.target.value === '' ? null : Math.max(1, Number(e.target.value) || 1) })}
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase">
                  <input type="checkbox" checked={selectedField.is_required} onChange={(e) => handleUpdateField({ ...selectedField, is_required: e.target.checked })} className="accent-primary" /> {t('customApps.studio.required', 'Required')}
                </label>
                <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase">
                  <input type="checkbox" checked={selectedField.show_in_list !== false} onChange={(e) => handleUpdateField({ ...selectedField, show_in_list: e.target.checked })} className="accent-primary" /> {t('customApps.studio.inList', 'In List')}
                </label>
                <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase">
                  <input type="checkbox" checked={selectedField.is_primary} onChange={(e) => handleUpdateField({ ...selectedField, is_primary: e.target.checked })} className="accent-primary" /> {t('customApps.studio.primary', 'Primary')}
                </label>
                <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase">
                  <input type="checkbox" checked={Boolean(selectedField.is_readonly)} onChange={(e) => handleUpdateField({ ...selectedField, is_readonly: e.target.checked })} className="accent-primary" /> {t('customApps.studio.readOnly', 'Read Only')}
                </label>
              </div>

              {selectedField.field_type === 'number' && (
                <div>
                  <PropLabel>{t('customApps.studio.numberFormat', 'Number Format')}</PropLabel>
                  <select className={propInputClass} value={selectedField.options?.number_format || 'integer'}
                    onChange={(e) => handleUpdateField({ ...selectedField, options: { ...selectedField.options, number_format: e.target.value } })}>
                    <option value="integer">{t('customApps.studio.formatInteger', 'Integer')}</option>
                    <option value="decimal">{t('customApps.studio.formatDecimal', 'Decimal (Float)')}</option>
                    <option value="currency">{t('customApps.studio.formatCurrency', 'Currency')}</option>
                  </select>
                </div>
              )}

              {selectedField.field_type === 'select' && (
                <div>
                  <PropLabel>{t('customApps.studio.choices', 'Choices (comma-separated)')}</PropLabel>
                  <ChoicesEditor
                    value={selectedField.options?.choices}
                    onChange={(choices) => handleUpdateField({ ...selectedField, options: { choices } })}
                  />
                </div>
              )}

              {selectedField.field_type === 'link' && (
                <div className="space-y-2">
                  <PropLabel>{t('customApps.studio.pointsTo', 'Points to')}</PropLabel>
                  <div className="flex gap-1">
                    {[['domain', t('customApps.studio.domain', 'Domain')], ['core_table', t('customApps.studio.systemTable', 'System Table')], ['custom_model', t('customApps.studio.customObject', 'Custom Object')]].map(([lt, lbl]) => (
                      <button key={lt} type="button"
                        onClick={() => handleUpdateField({ ...selectedField, options: { ...selectedField.options, link_type: lt } })}
                        className={`flex-1 px-1.5 py-1.5 rounded text-[8px] font-black uppercase ${(selectedField.options?.link_type || 'core_table') === lt ? 'bg-primary text-white' : 'bg-surface-panel text-gray-500'}`}
                      >{lbl}</button>
                    ))}
                  </div>
                  {selectedField.options?.link_type === 'domain' && (
                    <select className={propInputClass} value={selectedField.options?.domain_id || ''} onChange={(e) => handleUpdateField({ ...selectedField, options: { ...selectedField.options, domain_id: e.target.value } })}>
                      <option value="">— {t('customApps.studio.selectDomain', 'Select a domain')} —</option>
                      {domains.map((d) => <option key={d.domain_id} value={d.domain_id}>{d.domain_id}{d.description ? ` - ${d.description}` : ''}</option>)}
                    </select>
                  )}
                  {selectedField.options?.link_type === 'custom_model' && (
                    <select className={propInputClass} value={selectedField.options?.custom_model_slug || ''} onChange={(e) => handleUpdateField({ ...selectedField, options: { ...selectedField.options, custom_model_slug: e.target.value } })}>
                      <option value="">— {otherModels.length === 0 ? t('customApps.studio.noOtherObjects', 'No other objects yet') : t('customApps.studio.selectObject', 'Select an object')} —</option>
                      {otherModels.map((m) => <option key={m.slug} value={m.slug}>{m.label}</option>)}
                    </select>
                  )}
                  {(!selectedField.options?.link_type || selectedField.options?.link_type === 'core_table') && (
                    <>
                      <select className={propInputClass} value={selectedField.options?.core_table || ''} onChange={(e) => handleUpdateField({ ...selectedField, options: { ...selectedField.options, link_type: 'core_table', core_table: e.target.value } })}>
                        <option value="">— {t('customApps.studio.selectTable', 'Select a table')} —</option>
                        {CORE_TABLES.map((ct) => <option key={ct} value={ct}>{ct}</option>)}
                      </select>
                      <label className="flex items-center gap-1.5 text-[9px] font-bold text-gray-500 uppercase">
                        <input type="checkbox" checked={Boolean(selectedField.options?.filter_by_site)}
                          onChange={(e) => handleUpdateField({ ...selectedField, options: { ...selectedField.options, filter_by_site: e.target.checked } })}
                          className="accent-primary" />
                        {t('customApps.filterBySite', 'Filter results by current site')}
                      </label>
                    </>
                  )}
                </div>
              )}

              {selectedField.field_type === 'table' && (
                <div className="space-y-2">
                  <div className="flex gap-1">
                    {[['columns', t('customApps.studio.defineColumns', 'Define columns')], ['linked_model', t('customApps.studio.linkAnObject', 'Link an object')], ['linked_domain', t('customApps.studio.linkADomain', 'Link a Domain')]].map(([cm, lbl]) => (
                      <button key={cm} type="button"
                        onClick={() => handleUpdateField({ ...selectedField, options: { ...selectedField.options, child_mode: cm } })}
                        className={`flex-1 px-1.5 py-1.5 rounded text-[8px] font-black uppercase ${(selectedField.options?.child_mode || 'columns') === cm ? 'bg-primary text-white' : 'bg-surface-panel text-gray-500'}`}
                      >{lbl}</button>
                    ))}
                  </div>
                  {selectedField.options?.child_mode === 'linked_model' && (
                    <select className={propInputClass} value={selectedField.options?.linked_model_slug || ''} onChange={(e) => handleUpdateField({ ...selectedField, options: { ...selectedField.options, linked_model_slug: e.target.value } })}>
                      <option value="">— {otherModels.length === 0 ? t('customApps.studio.noOtherObjects', 'No other objects yet') : t('customApps.studio.selectObject', 'Select an object')} —</option>
                      {otherModels.map((m) => <option key={m.slug} value={m.slug}>{m.label}</option>)}
                    </select>
                  )}
                  {selectedField.options?.child_mode === 'linked_domain' && (
                    <select className={propInputClass} value={selectedField.options?.domain_id || ''} onChange={(e) => handleUpdateField({ ...selectedField, options: { ...selectedField.options, domain_id: e.target.value } })}>
                      <option value="">— {t('customApps.studio.selectDomain', 'Select a domain')} —</option>
                      {domains.map((d) => <option key={d.domain_id} value={d.domain_id}>{d.domain_id}{d.description ? ` - ${d.description}` : ''}</option>)}
                    </select>
                  )}
                  {(!selectedField.options?.child_mode || selectedField.options?.child_mode === 'columns') && (
                    <div className="space-y-2">
                      {(selectedField.options?.columns || []).map((col, idx) => {
                        const updateCol = (patch) => {
                          const columns = [...(selectedField.options?.columns || [])];
                          columns[idx] = { ...columns[idx], ...patch };
                          handleUpdateField({ ...selectedField, options: { ...selectedField.options, columns } });
                        };
                        const removeCol = () => {
                          const columns = (selectedField.options?.columns || []).filter((_, i) => i !== idx);
                          handleUpdateField({ ...selectedField, options: { ...selectedField.options, columns } });
                        };
                        return (
                          <div key={idx} className="border border-line-subtle rounded p-1.5 space-y-1.5">
                            <div className="flex items-center gap-2">
                              <input className="flex-1 border-b border-gray-200 outline-none py-1 text-xs font-bold min-w-0" placeholder={t('customApps.studio.columnLabel', 'Column label')} value={col.label}
                                onChange={(e) => updateCol({ label: e.target.value })} />
                              <select className="border-b border-gray-200 outline-none py-1 text-xs font-bold" value={col.field_type}
                                onChange={(e) => updateCol({ field_type: e.target.value })}>
                                <option value="text">{t('customApps.studio.typeText', 'Text')}</option><option value="number">{t('customApps.studio.typeNumber', 'Number')}</option>
                                <option value="date">{t('customApps.studio.typeDate', 'Date')}</option><option value="boolean">{t('customApps.studio.typeYesNo', 'Yes/No')}</option>
                                <option value="link">{t('customApps.studio.typeLink', 'Link')}</option>
                                <option value="select">{t('customApps.studio.typeList', 'List')}</option>
                              </select>
                              <button type="button" onClick={removeCol} className="text-gray-300 hover:text-status-danger shrink-0"><Trash2 size={12} /></button>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1.5">
                                <PropLabel>{t('customApps.studio.width', 'Width')}</PropLabel>
                                <input type="number" min="1" className="w-12 border-b border-gray-200 outline-none py-1 text-xs font-bold text-center"
                                  value={col.col_size || 1} onChange={(e) => updateCol({ col_size: Math.max(1, Number(e.target.value) || 1) })} />
                              </div>
                              <label className="flex items-center gap-1.5 text-[9px] font-bold text-gray-500 uppercase">
                                <input type="checkbox" checked={Boolean(col.is_readonly)} onChange={(e) => updateCol({ is_readonly: e.target.checked })} className="accent-primary" />
                                {t('customApps.studio.readOnly', 'Read Only')}
                              </label>
                              {col.field_type === 'number' && (
                                <select className="border-b border-gray-200 outline-none py-1 text-[10px] font-bold" value={col.number_format || 'integer'}
                                  onChange={(e) => updateCol({ number_format: e.target.value })}>
                                  <option value="integer">{t('customApps.studio.formatInteger', 'Integer')}</option>
                                  <option value="decimal">{t('customApps.studio.formatDecimal', 'Decimal (Float)')}</option>
                                  <option value="currency">{t('customApps.studio.formatCurrency', 'Currency')}</option>
                                </select>
                              )}
                            </div>
                            <TranslationsEditor translations={col.translations} onChange={(tr) => updateCol({ translations: tr })} />
                            {col.field_type === 'select' && (
                              <div className="pt-1 border-t border-line-subtle">
                                <PropLabel>{t('customApps.studio.choices', 'Choices (comma-separated)')}</PropLabel>
                                <ChoicesEditor value={col.choices} onChange={(choices) => updateCol({ choices })} />
                              </div>
                            )}
                            {col.field_type === 'link' && (
                              <div className="space-y-1.5 pt-1 border-t border-line-subtle">
                                <div className="flex gap-1">
                                  {[['domain', t('customApps.studio.domain', 'Domain')], ['core_table', t('customApps.studio.systemTable', 'System Table')], ['custom_model', t('customApps.studio.object', 'Object')]].map(([lt, lbl]) => (
                                    <button key={lt} type="button" onClick={() => updateCol({ link_type: lt })}
                                      className={`flex-1 px-1 py-1 rounded text-[7px] font-black uppercase ${(col.link_type || 'core_table') === lt ? 'bg-primary text-white' : 'bg-surface-panel text-gray-500'}`}
                                    >{lbl}</button>
                                  ))}
                                </div>
                                {col.link_type === 'domain' && (
                                  <select className="w-full border-b border-gray-200 outline-none py-1 text-[11px] font-bold" value={col.domain_id || ''} onChange={(e) => updateCol({ domain_id: e.target.value })}>
                                    <option value="">— {t('customApps.studio.selectDomain', 'Select a domain')} —</option>
                                    {domains.map((d) => <option key={d.domain_id} value={d.domain_id}>{d.domain_id}</option>)}
                                  </select>
                                )}
                                {col.link_type === 'custom_model' && (
                                  <select className="w-full border-b border-gray-200 outline-none py-1 text-[11px] font-bold" value={col.custom_model_slug || ''} onChange={(e) => updateCol({ custom_model_slug: e.target.value })}>
                                    <option value="">— {t('customApps.studio.selectObject', 'Select an object')} —</option>
                                    {otherModels.map((m) => <option key={m.slug} value={m.slug}>{m.label}</option>)}
                                  </select>
                                )}
                                {(!col.link_type || col.link_type === 'core_table') && (
                                  <>
                                    <select className="w-full border-b border-gray-200 outline-none py-1 text-[11px] font-bold" value={col.core_table || ''} onChange={(e) => updateCol({ link_type: 'core_table', core_table: e.target.value })}>
                                      <option value="">— {t('customApps.studio.selectTable', 'Select a table')} —</option>
                                      {CORE_TABLES.map((ct) => <option key={ct} value={ct}>{ct}</option>)}
                                    </select>
                                    <label className="flex items-center gap-1.5 text-[8px] font-bold text-gray-500 uppercase">
                                      <input type="checkbox" checked={Boolean(col.filter_by_site)} onChange={(e) => updateCol({ filter_by_site: e.target.checked })} className="accent-primary" />
                                      {t('customApps.filterBySite', 'Filter results by current site')}
                                    </label>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <button type="button" onClick={() => {
                        const columns = [...(selectedField.options?.columns || [])];
                        columns.push({ field_name: `col_${columns.length + 1}`, label: `Column ${columns.length + 1}`, field_type: 'text' });
                        handleUpdateField({ ...selectedField, options: { ...selectedField.options, columns } });
                      }} className="flex items-center gap-1 text-[10px] font-black uppercase text-primary hover:underline"><Plus size={12} /> {t('customApps.studio.addColumn', 'Add Column')}</button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomAppStudio;
